import { useState, useCallback, useEffect, useRef } from "react";
import "./MathPractice.css";
import { awardToken } from "../../awardtoken";

/* ──────── Constants & Helpers ──────── */
const SOUND_SOURCES = ["/sounds/correct.mp3", "/sounds/correct.ogg", "/sounds/correct.wav"];
const SHEET_SIZES = [10, 25, 50];

const MODES = {
  EXPONENTS: "exponents",
  SCI_NOT: "scientific",
  POLY: "polynomials",
  INEQ: "inequalities",
  LINEAR: "linear",
  QUADRATIC: "quadratic",
  RADICALS: "radicals"
};

const MODE_LABELS = {
  [MODES.EXPONENTS]: "Exponents & Roots（指數/平方根/立方根）",
  [MODES.SCI_NOT]: "Scientific Notation（科學記號運算）",
  [MODES.POLY]: "Polynomials & Factoring（多項式/因式分解）",
  [MODES.INEQ]: "Inequalities（不等式判斷/圖形化）",
  [MODES.LINEAR]: "Linear Equations（一元一次方程/代數應用）",
  [MODES.QUADRATIC]: "Quadratics（二次方程基礎）",
  [MODES.RADICALS]: "Radicals & Rational Expressions（根號/有理式）",
};

const DIFFICULTY = {
  EASY:   { label: "Easy" },
  MEDIUM: { label: "Medium" },
  HARD:   { label: "Hard" },
};


function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/* ──────── Problem Generators by Category ──────── */
function genProblemForMode(mode, diff = DIFFICULTY.MEDIUM) {
  switch (mode) {
    case MODES.EXPONENTS: {
      // Example: 2^3, sqrt(16), cbrt(27)
      const types = ["exp", "sqrt", "cbrt"];
      const t = types[randInt(0, types.length - 1)];
      if (t === "exp") {
        const base = randInt(2, 5), exp = randInt(2, 4);
        return {
          question: `Evaluate: <b>${base}<sup>${exp}</sup></b>`,
          answer: Math.pow(base, exp)
        };
      }
      if (t === "sqrt") {
        const root = randInt(2, 12);
        return {
          question: `Evaluate: <b>&radic;${root * root}</b>`,
          answer: root
        };
      }
      // cbrt
      const root = randInt(2, 5);
      return {
        question: `Evaluate: <b>&#8731;${root * root * root}</b>`,
        answer: root
      };
    }
    case MODES.SCI_NOT: {
      // Example: (2.5 x 10^3) x (4 x 10^2)
      const a = randInt(1, 9), b = randInt(1, 9), e1 = randInt(2, 4), e2 = randInt(2, 4);
      const op = Math.random() > 0.5 ? "×" : "÷";
      if (op === "×") {
        return {
          question: `Compute: <b>(${a} × 10<sup>${e1}</sup>) × (${b} × 10<sup>${e2}</sup>)</b> (in scientific notation)`,
          answer: `${a * b}e${e1 + e2}`
        };
      }
      return {
        question: `Compute: <b>(${a} × 10<sup>${e1}</sup>) ÷ (${b} × 10<sup>${e2}</sup>)</b> (in scientific notation)`,
        answer: `${(a / b).toFixed(2)}e${e1 - e2}`
      };
    }
    case MODES.POLY: {
      // Example: Expand (x+a)(x+b), or factor x^2+bx+c
      const a = randInt(1, 5), b = randInt(1, 5);
      return {
        question: `Expand: <b>(x+${a})(x+${b})</b>`,
        answer: `x^2+${a + b}x+${a * b}`
      };
    }
    case MODES.INEQ: {
      // Example: 3x+2 > 11
      const coef = randInt(2, 5), c = randInt(1, 10), rhs = randInt(11, 20);
      return {
        question: `Solve for x: <b>${coef}x + ${c} > ${rhs}</b>`,
        answer: `x > ${((rhs - c) / coef).toFixed(2)}`
      };
    }
    case MODES.LINEAR: {
      // Example: 2x+3=11
      const coef = randInt(1, 5), c = randInt(1, 10), rhs = randInt(11, 20);
      return {
        question: `Solve: <b>${coef}x + ${c} = ${rhs}</b>`,
        answer: ((rhs - c) / coef).toFixed(2)
      };
    }
    case MODES.QUADRATIC: {
      // x^2+bx+c=0, b,c random, real roots only for now
      const b = randInt(-10, 10), c = randInt(1, 20);
      const D = b * b - 4 * 1 * c;
      if (D < 0) return genProblemForMode(mode, diff); // regen if no real roots
      const x1 = ((-b + Math.sqrt(D)) / 2).toFixed(2);
      const x2 = ((-b - Math.sqrt(D)) / 2).toFixed(2);
      return {
        question: `Solve: <b>x² + ${b >= 0 ? "+" : ""}${b}x + ${c} = 0</b> (Give one root)`,
        answer: x1 // Accept either root, but for now x1
      };
    }
    case MODES.RADICALS: {
      // Example: Simplify sqrt(50)
      const n = [18, 24, 32, 45, 50, 72, 98][randInt(0, 6)];
      return {
        question: `Simplify: <b>&radic;${n}</b>`,
        answer: {
          18: "3√2", 24: "2√6", 32: "4√2", 45: "3√5", 50: "5√2", 72: "6√2", 98: "7√2"
        }[n]
      };
    }
    default:
      return { question: "No problem found", answer: "" };
  }
}

const genProblem = ({ mode, diff }) => genProblemForMode(mode, diff);

const genSet = (qty, options) => Array.from({ length: qty }, () => genProblem(options));

/* ──────── Main Component ──────── */
export default function MathAlgebraPractice({ onAwardTokens } = {}) {
  const [ans, setAns] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [correct, setCorrect]  = useState(0);
  const [sheet, setSheet] = useState(null);
  const [mode, setMode] = useState(MODES.EXPONENTS);
  const [difficulty, setDifficulty] = useState(DIFFICULTY.MEDIUM);
  const [problem, setProblem] = useState(() => genProblem({ mode, diff: difficulty }));
  const [feedback, setFeedback] = useState(null);
  const [waitingForNext, setWaitingForNext] = useState(false);

  const audioRef = useRef(null);
  const tokensAwarded = useRef(0);

  useEffect(() => {
    if (correct >= (tokensAwarded.current + 1) * 10) {
      awardToken();
      tokensAwarded.current += 1;
      if (onAwardTokens) onAwardTokens(1);
    }
  }, [correct, onAwardTokens]);

  useEffect(() => {
    setProblem(genProblem({ mode, diff: difficulty }));
  }, [mode, difficulty]);

  useEffect(() => {
    const audio = new Audio();
    for (const src of SOUND_SOURCES) {
      const type = src.endsWith(".mp3") ? "audio/mpeg"
        : src.endsWith(".ogg") ? "audio/ogg"
        : "audio/wav";
      if (audio.canPlayType(type)) { audio.src = src; break; }
    }
    audioRef.current = audio;
  }, []);

  const resetStats = useCallback(() => { setAttempts(0); setCorrect(0); }, []);
  const nextProblem = () => setProblem(genProblem({ mode, diff: difficulty }));

const submit = (e) => {
  e.preventDefault();
  if (!ans.trim() || waitingForNext) return;
  const correctAns = problem.answer;
  let ok = false;

  // 科學記號專屬比對
  if (mode === MODES.SCI_NOT) {
    const normalizeSciNotation = ans =>
      ans.replace(/\s+/g, "")
         .replace(/[×x*]/gi, "e")
         .replace(/\^/, "");
    const userAnsNorm = normalizeSciNotation(ans.toLowerCase());
    const correctAnsNorm = normalizeSciNotation(String(correctAns).toLowerCase());
    ok =
      userAnsNorm === correctAnsNorm ||
      parseFloat(ans) === parseFloat(correctAns) ||
      parseFloat(ans) === parseFloat(eval(String(correctAns).replace("e", "*1e")));
  } else if (typeof correctAns === "number") {
    ok = Math.abs(parseFloat(ans) - correctAns) < 0.01;
  } else if (typeof correctAns === "string") {
    ok = ans.replace(/\s+/g, "").toLowerCase() === correctAns.replace(/\s+/g, "").toLowerCase();
  }

  setAttempts((n) => n + 1);
  if (ok) {
    setCorrect((n) => n + 1);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    }
    setFeedback("✅ Correct!");
    setAns("");
    setTimeout(() => {
      setFeedback(null);
      nextProblem();
    }, 700);
    setWaitingForNext(false);
  } else {
    setFeedback(`❌ The correct answer is: ${problem.answer}`);
    setWaitingForNext(true);
  }
};


  const startSheet = (n) => {
    resetStats();
    setSheet(genSet(n, { mode, diff: difficulty }));
    setTimeout(() => window.print(), 50);
  };

  if (sheet) {
    return (
      <div className="worksheet">
        <h2 className="no-print" style={{ display: "flex", justifyContent: "space-between" }}>
          Algebra Practice Worksheet
          <span>
            <button onClick={() => setSheet(null)} style={{ marginLeft: "0.5rem" }}>Back to Practice</button>
          </span>
        </h2>
        <div className="sheet-grid">
          {sheet.map((p, i) => (
            <div className="math-col" key={i}>
              <p className="geometry-question" dangerouslySetInnerHTML={{ __html: p.question }} />
              <div className="math-underline" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="practice-container">
      <h2 style={{ display: "flex", justifyContent: "space-between" }}>
        Algebra Practice
      </h2>
      {/* Controls */}
      <div className="control-row">
        <label>
          Topic:&nbsp;
          <select value={mode} onChange={e => setMode(e.target.value)}>
            {Object.entries(MODE_LABELS).map(([key, label]) =>
              <option key={key} value={key}>{label}</option>
            )}
          </select>
        </label>
        <br />
        <label style={{ marginLeft: "1rem" }}>
          Difficulty:&nbsp;
          <select value={difficulty.label}
            onChange={e => setDifficulty(DIFFICULTY[e.target.value])}
          >
            {Object.entries(DIFFICULTY).map(([key, obj]) =>
              <option key={key} value={key}>{obj.label}</option>
            )}
          </select>
        </label>
      </div>
      {/* Problem */}
      <div className="current-question" dangerouslySetInnerHTML={{ __html: problem.question }} />
      <form onSubmit={submit} className="answer-form">
        <input
          type="text"
          value={ans}
          onChange={e => setAns(e.target.value)}
          autoFocus
        />
        <button type="submit">Submit</button>
      </form>
      {feedback && (
        <div className="math-practice-feedback" style={{ marginBottom: 7 }}>
          {feedback}
        </div>
      )}
      {waitingForNext && (
        <button
          className="math-pill-btn"
          style={{ marginTop: "0.7em" }}
          onClick={() => {
            setFeedback(null);
            setAns("");
            setWaitingForNext(false);
            nextProblem();
          }}
        >
          Next
        </button>
      )}
      <p>Score: {correct} / {attempts}</p>
      <div className="worksheet-buttons">
        <strong>Printable worksheets:</strong>
        {SHEET_SIZES.map((n) => (
          <button key={n} onClick={() => startSheet(n)}>{n} Questions</button>
        ))}
      </div>
    </div>
  );
}
